/**
 * Trace-header propagation allowlist (FQDN whitelist)
 * ---------------------------------------------------
 *
 * Single source of truth for the cross-origin hosts that PSP is allowed to send
 * the W3C distributed-tracing headers (`traceparent` / `tracestate`) to.
 *
 * ## Why this file exists
 *
 * Faro's `TracingInstrumentation` injects `traceparent`/`tracestate` into a request
 * when EITHER:
 *   1. the request is same-origin (`psp.<env>` — always traced, never listed here), OR
 *   2. the request URL matches an entry in `propagateTraceHeaderCorsUrls`.
 *
 * A host only tolerates those headers if its CORS preflight returns them in
 * `Access-Control-Allow-Headers`. Sending them to a host that does NOT allow them
 * fails the preflight (`status=0`) and BREAKS the request.
 *
 * This bit us on 2026-06-23: PR #385 propagated to a broad `*.opengov.com` (etc.)
 * regex, which swept in `*.eamclassic.opengov.com` — those hosts reject the headers,
 * so EAM broke and the change was reverted in #391. This file replaces that wildcard
 * with an explicit, per-environment list of hosts we have actually validated.
 *
 * ## Source of truth for the lists below
 *
 * Only hosts confirmed ✅ Allows (zero `status=0`) are listed here. The FULL host
 * inventory — including ⚠️ Intermittent, ❌ Blocking and ❓ Unknown hosts, with their
 * measured failure rates — lives in the feature doc:
 *   .feature-docs/2026-06-24-mmahadevan-traceparent-cors-allowlist.md
 * Promote a host here only after the team confirms its CORS allows the headers.
 *
 * NEVER add:
 *   - non-OpenGov domains (LaunchDarkly, FullStory, Segment, Pendo, PubNub, ArcGIS,
 *     Gravatar, CloudFront, …) — we cannot change their CORS config.
 *   - `login.*` auth hosts — high blast radius; keep excluded until a deliberate,
 *     separately-validated decision is made.
 *   - any `*.eamclassic.opengov.com` host — the family that caused the 2026-06-23
 *     incident.
 */

import { HostingEnvironment } from '@opengov/psp-contract';

/**
 * Permitting & Licensing (PLC) service hosts the mounted PLC app calls. Each was preflighted
 * from the shell origin at the path PLC requests, with and without `traceparent, tracestate`,
 * on 2026-09-28; only hosts that allowed both are listed.
 *
 * Deliberately absent:
 *   - `reports-service.*`: its CORS allows a fixed header list without the trace headers.
 *   - `api.plce.opengov.engineering`: it sends no `Access-Control-Allow-Origin` for the
 *     staging shell at all.
 *   - PLC's storage, control-panel and OG Assist hosts: not PLC API calls, or not called from
 *     the shell.
 */
const PLC_PRODUCTION_HOSTS = [
  'access-control.viewpointcloud.com',
  'activities.viewpointcloud.com',
  'api-east.viewpointcloud.com',
  'api.plce.opengov.com',
  'comment-library.viewpointcloud.com',
  'contacts.viewpointcloud.com',
  'employee-east.viewpointcloud.io',
  'explore-reports.viewpointcloud.com',
  'form-data.viewpointcloud.com',
  'form-templates.viewpointcloud.com',
  'forms.viewpointcloud.com',
  'inbox.viewpointcloud.com',
  'ingest.viewpointcloud.com',
  'inspections.viewpointcloud.com',
  'locations.viewpointcloud.com',
  'notifications.viewpointcloud.com',
  'payments.viewpointcloud.com',
  'plc-service-assist.viewpointcloud.com',
  'ppf-backend.viewpointcloud.com',
  'rate-library.viewpointcloud.com',
  'record-types.viewpointcloud.com',
  'records.viewpointcloud.com',
  'review-step.viewpointcloud.com',
  'search.viewpointcloud.com',
  'users.viewpointcloud.com',
  'workflows.viewpointcloud.com',
] as const;

const PLC_STAGING_HOSTS = [
  'access-control.vpcstaging.com',
  'activities.vpcstaging.com',
  'api01.vpcstaging.com',
  'comment-library.vpcstaging.com',
  'contacts.vpcstaging.com',
  'employee.vpcstaging.io',
  'explore-reports.vpcstaging.com',
  'form-data.vpcstaging.com',
  'form-templates.vpcstaging.com',
  'forms.vpcstaging.com',
  'inbox.vpcstaging.com',
  'ingest.vpcstaging.com',
  'inspections.vpcstaging.com',
  'locations.vpcstaging.com',
  'notifications.vpcstaging.com',
  'payments.vpcstaging.com',
  'plc-service-assist.vpcstaging.com',
  'ppf-backend.vpcstaging.com',
  'rate-library.vpcstaging.com',
  'record-types.vpcstaging.com',
  'records.vpcstaging.com',
  'review-step.vpcstaging.com',
  'search.vpcstaging.com',
  'users.vpcstaging.com',
  'workflows.vpcstaging.com',
] as const;

/**
 * Validated, OpenGov-owned hosts allowed to receive trace headers, per environment.
 *
 * Entries are bare fully-qualified domain names (no scheme, no port, no path).
 * They are compiled into host-anchored regexes by {@link buildTraceHeaderCorsPatterns}.
 *
 * Same-origin `psp.<env>` requests are always traced by the instrumentation and are
 * intentionally NOT listed here.
 */
export const TRACE_HEADER_ALLOWLIST: Readonly<Record<HostingEnvironment, readonly string[]>> = {
  [HostingEnvironment.PRODUCTION]: [
    'beacon.opengov.com',
    'taskmaster.opengov.com',
    'api.vendor.opengov.com',
    ...PLC_PRODUCTION_HOSTS,
  ],
  [HostingEnvironment.STAGING]: [
    'beacon.ogstaging.us',
    'taskmaster.ogstaging.us',
    'api.vendor.ogstaging.us',
    ...PLC_STAGING_HOSTS,
  ],
  [HostingEnvironment.INTEGRATION]: [
    'beacon.ogintegration.us',
    'taskmaster.ogintegration.us',
    'api-vendor.procurement.ogintegration.us',
  ],
  // Local dev serves PSP from psp.ogov.me (same-origin, always traced) and points
  // its API calls at the integration domain (see getHostingEnvironmentDomain), so
  // development reuses the integration allowlist.
  [HostingEnvironment.DEVELOPMENT]: [
    'beacon.ogintegration.us',
    'taskmaster.ogintegration.us',
    'api-vendor.procurement.ogintegration.us',
  ],
};

/** Escapes a string for safe literal use inside a RegExp. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Compiles a single FQDN into a host-anchored regex.
 *
 * `urlMatches` (OpenTelemetry) compares string patterns with strict equality against
 * the FULL url (including path), so a bare FQDN string never matches a real request.
 * We therefore match on host only and require a port, path, query, fragment, or
 * end-of-string boundary so `app.opengov.com` cannot match `app.opengov.com.evil.io`.
 */
function fqdnToPattern(fqdn: string): RegExp {
  return new RegExp(`^https?://${escapeRegExp(fqdn)}(?::\\d+)?(?:[/?#]|$)`, 'i');
}

/**
 * Builds the `propagateTraceHeaderCorsUrls` value for the given environment from the
 * validated FQDN allowlist. Pass the result straight into
 * `TracingInstrumentation`'s `instrumentationOptions.propagateTraceHeaderCorsUrls`.
 */
export function buildTraceHeaderCorsPatterns(environment: HostingEnvironment): RegExp[] {
  return TRACE_HEADER_ALLOWLIST[environment].map(fqdnToPattern);
}
